import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

import { applyGeoFilterAndNavigate } from '@/pages/klienci/common/klienciFiltersStorage'
import useGminaData from './hooks/useGminaData'
import useGminaModal from './hooks/useGminaModal'
import SearchActionsBar from '@/widgets/SearchActionsBar'
import LoadMoreSection from '@/widgets/LoadMoreSection'
import GminaTable from './components/GminaTable'
import GminaModal from './components/GminaModal'
import PowiatFilterModal from '@/filter-modals/components/PowiatFilterModal'
import WojewodztwoFilterModal from '@/filter-modals/components/WojewodztwoFilterModal'

export default function Gmina() {
	const navigate = useNavigate()
	const [isPowiatFilterOpen, setIsPowiatFilterOpen] = useState(false)
	const [isWojewodztwoFilterOpen, setIsWojewodztwoFilterOpen] = useState(false)

	const {
		data,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData,
		handleLoadMore,
		setSorting,
		updateFilters,
		sortColumn,
		sortDirection,
		searchQuery,
		filters,
		clearError,
	} = useGminaData()

	const {
		isModalOpen,
		editingItem,
		formName,
		setFormName,
		formPowiatId,
		formPowiatName,
		formWojewodztwoId,
		formWojewodztwoName,
		isSaving,
		isPowiatSelectOpen,
		isWojewodztwoSelectOpen,
		closeModal,
		handleSubmit,
		openPowiatSelectModal,
		closePowiatSelectModal,
		applyPowiatSelection,
		openWojewodztwoSelectModal,
		closeWojewodztwoSelectModal,
		applyWojewodztwoSelection,
		buttons,
	} = useGminaModal(loadData)

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: 'Error',
				text: error,
				icon: 'error',
			}).then(() => {
				clearError()
			})
		}
	}, [error, clearError])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		updateFilters({ searchText: e.target.value })
	}

	const selectedPowiatId = filters.id_powiat || null
	const selectedWojewodztwoId = filters.id_wojewodztwo || null
	const hasPowiatFilter = !!selectedPowiatId
	const hasWojewodztwoFilter = !!selectedWojewodztwoId

	const handleApplyPowiatFilter = powiatId => {
		updateFilters({ id_powiat: powiatId || null })
		setIsPowiatFilterOpen(false)
	}

	const handleApplyWojewodztwoFilter = wojewodztwoId => {
		const nextWojewodztwoId = wojewodztwoId || null
		updateFilters({
			id_wojewodztwo: nextWojewodztwoId,
			...(nextWojewodztwoId !== selectedWojewodztwoId && { id_powiat: null }),
		})
		setIsWojewodztwoFilterOpen(false)
	}

	const handleNazwaClick = gminaId =>
		applyGeoFilterAndNavigate(navigate, {
			targetPageKey: 'miasto',
			path: '/klienci/miasto',
			filterKey: 'id_gmina',
			filterValue: gminaId,
		})

	return (
		<>
			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder="Szukaj gmin po nazwie..."
				buttons={buttons}
			/>

			<GminaTable
				data={data}
				loading={loading}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
				hasPowiatFilter={hasPowiatFilter}
				hasWojewodztwoFilter={hasWojewodztwoFilter}
				onOpenPowiatFilter={() => setIsPowiatFilterOpen(true)}
				onOpenWojewodztwoFilter={() => setIsWojewodztwoFilterOpen(true)}
				onPowiatFilter={handleApplyPowiatFilter}
				onWojewodztwoFilter={handleApplyWojewodztwoFilter}
				onNazwaClick={handleNazwaClick}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={data.length}
				totalCount={totalCount}
			/>
			<GminaModal
				isOpen={isModalOpen}
				editingItem={editingItem}
				formName={formName}
				setFormName={setFormName}
				formPowiatId={formPowiatId}
				formPowiatName={formPowiatName}
				onOpenPowiatSelect={openPowiatSelectModal}
				formWojewodztwoId={formWojewodztwoId}
				formWojewodztwoName={formWojewodztwoName}
				onOpenWojewodztwoSelect={openWojewodztwoSelectModal}
				isSaving={isSaving}
				onClose={closeModal}
				onSubmit={handleSubmit}
			/>
			<PowiatFilterModal
				isOpen={isPowiatFilterOpen}
				onClose={() => setIsPowiatFilterOpen(false)}
				selectedPowiatId={selectedPowiatId}
				idWojewodztwo={selectedWojewodztwoId}
				onApply={handleApplyPowiatFilter}
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoFilterOpen}
				onClose={() => setIsWojewodztwoFilterOpen(false)}
				selectedWojewodztwoId={selectedWojewodztwoId}
				onApply={handleApplyWojewodztwoFilter}
			/>
			<PowiatFilterModal
				isOpen={isPowiatSelectOpen}
				onClose={closePowiatSelectModal}
				selectedPowiatId={formPowiatId}
				idWojewodztwo={formWojewodztwoId}
				onApply={applyPowiatSelection}
				title="Wybierz powiat"
				description="Wyszukaj i wybierz powiat dla gminy."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
			<WojewodztwoFilterModal
				isOpen={isWojewodztwoSelectOpen}
				onClose={closeWojewodztwoSelectModal}
				selectedWojewodztwoId={formWojewodztwoId}
				onApply={applyWojewodztwoSelection}
				title="Wybierz wojewodztwo"
				description="Wyszukaj i wybierz wojewodztwo dla gminy."
				applyLabel="Wybierz"
				clearLabel="Wyczysc wybor"
			/>
		</>
	)
}
